import React, { useEffect, useRef, useState, useCallback } from 'react';

export default function HLSPlayer({ hlsUrl, live, streamKey, serverHost }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const pcRef = useRef(null);
  const [state, setState] = useState('idle'); // idle | loading | playing | error
  const [mode, setMode] = useState('hls'); // hls | webrtc
  const [quality, setQuality] = useState('auto'); // auto | 1080p | 720p | 480p
  const [availableQualities, setAvailableQualities] = useState([]);
  const [bitrate, setBitrate] = useState(null);

  const host = serverHost || window.location.host;
  const masterUrl = streamKey ? `/api/abr/${streamKey}/master.m3u8` : hlsUrl;

  // Check available variants
  const checkVariants = useCallback(async () => {
    if (!streamKey) return;
    try {
      const res = await fetch(`/api/abr/${streamKey}/status`);
      const data = await res.json();
      const qualities = ['auto'];
      if (data.variants?.original) qualities.push('1080p');
      if (data.variants?.['720p']) qualities.push('720p');
      if (data.variants?.['480p']) qualities.push('480p');
      setAvailableQualities(qualities);
    } catch {}
  }, [streamKey]);

  const stopAll = useCallback(() => {
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    if (videoRef.current) { videoRef.current.src = ''; videoRef.current.load(); }
  }, []);

  // Start WebRTC playback via WHEP
  const startWebRTC = useCallback(async () => {
    if (!streamKey) return;
    setState('loading');
    try {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      pcRef.current = pc;

      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Wait for ICE gathering
      await new Promise(resolve => {
        if (pc.iceGatheringState === 'complete') { resolve(); return; }
        pc.addEventListener('icegatheringstatechange', () => {
          if (pc.iceGatheringState === 'complete') resolve();
        });
        setTimeout(resolve, 3000); // timeout after 3s
      });

      // Send offer to SRS WHEP endpoint
      // SRS uses its own WebRTC play API (not standard WHEP)
      const playUrl = `https://${host}/rtc/v1/play/`;
      const response = await fetch(playUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api: playUrl,
          streamurl: `webrtc://${host}/live/${streamKey}`,
          sdp: pc.localDescription.sdp
        }),
      });

      if (!response.ok) throw new Error(`WebRTC play failed: ${response.status}`);

      const data = await response.json();
      if (data.code !== 0) throw new Error(`SRS error: ${data.code}`);
      await pc.setRemoteDescription({ type: 'answer', sdp: data.sdp });

      pc.ontrack = (e) => {
        if (videoRef.current && e.streams[0]) {
          videoRef.current.srcObject = e.streams[0];
          videoRef.current.play().catch(() => {});
          setState('playing');
        }
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          console.log('[webrtc] Connection failed, falling back to HLS');
          setMode('hls');
        }
      };
    } catch (err) {
      console.log('[webrtc] Failed, falling back to HLS:', err.message);
      setMode('hls');
    }
  }, [streamKey, host]);

  // Start HLS playback
  const startHLS = useCallback(async (qualityOverride) => {
    if (!live) { setState('idle'); return; }
    setState('loading');
    let destroyed = false;

    const init = async () => {
      await new Promise(r => setTimeout(r, 4000));
      if (destroyed) return;

      // Load HLS.js if not present
      if (!window.Hls) {
        await new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.7/hls.min.js';
          s.onload = res; s.onerror = rej;
          document.head.appendChild(s);
        });
      }

      const Hls = window.Hls;
      const video = videoRef.current;
      if (!video || destroyed) return;

      // Pick URL based on quality
      let url = hlsUrl;
      if (quality === 'auto' || qualityOverride === 'auto') {
        // Try master playlist first for ABR
        url = masterUrl;
      } else if (quality === '720p' || qualityOverride === '720p') {
        url = hlsUrl?.replace('.m3u8', '_720p.m3u8') || hlsUrl;
      } else if (quality === '480p' || qualityOverride === '480p') {
        url = hlsUrl?.replace('.m3u8', '_480p.m3u8') || hlsUrl;
      }

      if (Hls.isSupported()) {
        if (hlsRef.current) hlsRef.current.destroy();
        const hls = new Hls({
          liveSyncDurationCount: 2,
          lowLatencyMode: true,
          enableWorker: true,
        });

        hls.loadSource(url);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, (e, data) => {
          if (!destroyed) {
            setState('playing');
            video.play().catch(() => {});
            // Update available qualities from manifest
            if (data.levels?.length > 1) {
              checkVariants();
            }
          }
        });

        hls.on(Hls.Events.ERROR, (e, data) => {
          if (data.fatal) {
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR && url === masterUrl) {
              // Master playlist not available, fall back to direct URL
              hls.loadSource(hlsUrl);
            } else {
              setState('error');
            }
          }
        });

        // Track bitrate
        hls.on(Hls.Events.FRAG_LOADED, (e, data) => {
          const kbps = Math.round((data.frag.loaded * 8) / (data.stats.loading.end - data.stats.loading.start));
          if (kbps > 0) setBitrate(kbps);
        });

        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url;
        video.play().catch(() => {});
        setState('playing');
      }
    };

    init();
    return () => { destroyed = true; };
  }, [live, hlsUrl, masterUrl, quality, checkVariants]);

  // Handle mode/quality changes
  useEffect(() => {
    if (!live) { stopAll(); setState('idle'); return; }
    stopAll();
    if (mode === 'webrtc') {
      startWebRTC();
    } else {
      startHLS(quality);
    }
  }, [live, mode, quality]);

  // Check variants when stream starts
  useEffect(() => {
    if (live && streamKey) {
      setTimeout(checkVariants, 6000); // check after segments start
    }
  }, [live, streamKey, checkVariants]);

  const s = {
    wrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: '16px' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--border)' },
    title: { fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px' },
    controls: { display: 'flex', alignItems: 'center', gap: '6px' },
    modeBtns: { display: 'flex', gap: '4px' },
    modeBtn: (active) => ({ fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '5px', cursor: 'pointer', background: active ? 'var(--signal)' : 'var(--surface2)', color: active ? '#000' : 'var(--text-muted)', border: `1px solid ${active ? 'var(--signal)' : 'var(--border)'}` }),
    qualitySelect: { fontSize: '11px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '5px', color: 'var(--text)', padding: '3px 6px', cursor: 'pointer' },
    livePill: { fontSize: '11px', fontWeight: '800', padding: '3px 9px', borderRadius: '20px', background: 'rgba(0,230,118,0.12)', color: 'var(--signal)', border: '1px solid rgba(0,230,118,0.25)' },
    screen: { position: 'relative', background: '#000', aspectRatio: '16/9' },
    video: { width: '100%', height: '100%', display: 'block' },
    overlay: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', color: 'var(--text-muted)', fontSize: '14px', textAlign: 'center', padding: '20px' },
    bitrateTag: { position: 'absolute', bottom: '8px', right: '8px', fontSize: '10px', fontFamily: 'var(--mono)', background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '2px 6px', borderRadius: '4px' },
    retryBtn: { padding: '7px 18px', background: 'var(--signal)', color: '#000', fontWeight: '700', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px' },
  };

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <span style={s.title}>Live Preview</span>
        <div style={s.controls}>
          {/* Quality selector */}
          {live && availableQualities.length > 1 && mode === 'hls' && (
            <select style={s.qualitySelect} value={quality} onChange={e => setQuality(e.target.value)}>
              {availableQualities.map(q => (
                <option key={q} value={q}>{q === 'auto' ? 'Auto (ABR)' : q}</option>
              ))}
            </select>
          )}
          {/* Mode switcher */}
          {live && (
            <div style={s.modeBtns}>
              <button style={s.modeBtn(mode === 'hls')} onClick={() => setMode('hls')}>HLS</button>
              <button style={s.modeBtn(mode === 'webrtc')} onClick={() => setMode('webrtc')}>WebRTC</button>
            </div>
          )}
          {live && <span style={s.livePill}>● LIVE</span>}
        </div>
      </div>

      <div style={s.screen}>
        <video ref={videoRef} style={s.video} controls muted playsInline />

        {!live && (
          <div style={s.overlay}>
            <div style={{ fontSize: '32px' }}>📡</div>
            <div style={{ fontWeight: '600', color: 'var(--text)' }}>Stream Offline</div>
            <div>Start streaming to see preview</div>
          </div>
        )}

        {live && state === 'loading' && (
          <div style={s.overlay}>
            <div style={{ fontSize: '28px' }}>⏳</div>
            <div style={{ fontWeight: '600', color: 'var(--text)' }}>
              {mode === 'webrtc' ? 'Connecting WebRTC…' : 'Buffering…'}
            </div>
            <div style={{ fontSize: '13px' }}>
              {mode === 'webrtc' ? 'Ultra-low latency mode' : 'Preview loads in ~5 seconds'}
            </div>
          </div>
        )}

        {live && state === 'error' && (
          <div style={s.overlay}>
            <div style={{ fontSize: '28px' }}>⚠️</div>
            <div style={{ fontWeight: '600', color: 'var(--text)' }}>Preview unavailable</div>
            <button style={s.retryBtn} onClick={() => { stopAll(); startHLS('auto'); }}>Retry</button>
          </div>
        )}

        {live && state === 'playing' && bitrate && (
          <div style={s.bitrateTag}>
            {mode === 'webrtc' ? '⚡ WebRTC' : `${bitrate} kbps`}
            {quality !== 'auto' && mode === 'hls' && ` · ${quality}`}
          </div>
        )}
      </div>
    </div>
  );
}
